import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { MAX_DENY_LIST_ITEMS } from '@meal-planner/shared';

interface DenyListItem { id: string; item: string; }

export function DenyListPage() {
  const qc = useQueryClient();
  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState('');

  const { data: items = [], isLoading } = useQuery<DenyListItem[]>({
    queryKey: ['deny-list'],
    queryFn: () => api.get('/api/deny-list'),
  });

  const addItem = useMutation({
    mutationFn: (item: string) => api.post('/api/deny-list', { item }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['deny-list'] }); setNewItem(''); setError(''); },
    onError: (err: any) => setError(err.body?.error ?? 'Failed to add item.'),
  });

  const removeItem = useMutation({
    mutationFn: (id: string) => api.delete(`/api/deny-list/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deny-list'] }),
  });

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = newItem.trim();
    if (!trimmed) return;
    if (trimmed.length > 100) { setError('Item must be 100 characters or less.'); return; }
    if (items.length >= MAX_DENY_LIST_ITEMS) { setError(`Cannot exceed ${MAX_DENY_LIST_ITEMS} items.`); return; }
    addItem.mutate(trimmed);
  }

  const usagePct = Math.min(100, (items.length / MAX_DENY_LIST_ITEMS) * 100);
  const atLimit = items.length >= MAX_DENY_LIST_ITEMS;

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="mb-8" style={{ maxWidth: 720 }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="ban" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Avoid list
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
            Foods to{' '}
            <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>skip.</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)' }}>
            Anything on this list is filtered out of every plan we generate for you.
          </p>
        </div>

        {/* ── Add card with usage bar ────────────────────────────────── */}
        <div className="card p-6 mb-8" style={{ maxWidth: 640 }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2" style={{ color: 'var(--ink-3)' }}>
              <Icon name="plus" size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ letterSpacing: '0.1em' }}>
                Add an item
              </span>
            </div>
            <div className="font-mono text-xs" style={{ color: 'var(--ink-4)' }}>
              {items.length}<span style={{ opacity: 0.6 }}> / {MAX_DENY_LIST_ITEMS}</span>
            </div>
          </div>

          <div className="h-1 rounded-full overflow-hidden mb-4" style={{ background: 'var(--surface-2)' }}>
            <div className="h-full transition-all duration-300"
              style={{
                width: `${usagePct}%`,
                background: atLimit ? 'var(--danger)' : 'var(--accent)',
              }} />
          </div>

          <form onSubmit={handleAdd} className="flex gap-2.5">
            <input
              type="text"
              placeholder="e.g. mushrooms, cilantro…"
              value={newItem}
              maxLength={100}
              onChange={(e) => setNewItem(e.target.value)}
              disabled={atLimit}
              style={{
                flex: 1,
                background: 'var(--surface-2)',
                border: '1px solid var(--line-soft)',
                borderRadius: 12,
                padding: '10px 14px',
                fontSize: 14,
                color: 'var(--ink)',
                fontFamily: 'inherit',
                outline: 'none',
                opacity: atLimit ? 0.5 : 1,
              }}
            />
            <button
              type="submit"
              disabled={addItem.isPending || atLimit}
              className="btn btn-primary"
              style={{ padding: '10px 18px', fontSize: 13.5, opacity: (addItem.isPending || atLimit) ? 0.6 : 1 }}
            >
              <Icon name="plus" size={14} stroke={2.4} />
              Add
            </button>
          </form>
          {error && (
            <p className="text-xs mt-3" style={{ color: 'var(--danger)' }}>{error}</p>
          )}
          {atLimit && !error && (
            <p className="text-xs mt-3" style={{ color: 'var(--warning)' }}>
              You've reached the {MAX_DENY_LIST_ITEMS}-item limit. Remove something to add more.
            </p>
          )}
        </div>

        {/* ── Item list ──────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="flex flex-wrap gap-2" style={{ maxWidth: 720 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse rounded-full"
                style={{ background: 'var(--surface-2)', height: 32, width: 100 + (i * 18) }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="card p-12 text-center" style={{ maxWidth: 560 }}>
            <div className="inline-flex items-center justify-center mb-4 rounded-full"
              style={{ width: 56, height: 56, background: 'var(--accent-soft)', color: 'var(--accent)' }}>
              <Icon name="ban" size={24} />
            </div>
            <p className="h-serif" style={{ fontSize: 18, color: 'var(--ink)', margin: 0, marginBottom: 4 }}>
              Nothing on your avoid list
            </p>
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              Add an ingredient or dish above and we'll keep it out of every plan.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" style={{ maxWidth: 880 }}>
            {items.map((item) => (
              <span key={item.id}
                className="inline-flex items-center gap-2 rounded-full"
                style={{
                  padding: '6px 6px 6px 14px',
                  background: 'color-mix(in oklab, var(--danger) 14%, var(--surface))',
                  border: '1px solid color-mix(in oklab, var(--danger) 30%, var(--line-soft))',
                  color: 'var(--ink)',
                  fontSize: 13,
                }}>
                {item.item}
                <button
                  onClick={() => removeItem.mutate(item.id)}
                  aria-label={`Remove ${item.item} from avoid list`}
                  className="inline-flex items-center justify-center rounded-full transition-colors"
                  style={{
                    width: 22, height: 22,
                    color: 'var(--danger)',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'color-mix(in oklab, var(--danger) 22%, transparent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Icon name="x" size={12} stroke={2.4} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
